import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Paper } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useResources } from "../../hooks/useRedux";
import { setResourcesUploadProgress, clearResourcesUploadProgress, createResource as createResourceAction } from "../../store";
import ResourceForm from "./ResourceForm";
import { Resource } from "../../store/slices/types";
import { endPoints } from "../../services/endpoints";
import { API_BASE_URL } from "../../config/api";
import axios from "axios";

const AddResource: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { uploadProgress } = useResources();
  const [saving, setSaving] = useState(false);

  const handleSave = async (resourceData: Partial<Resource>, mediaFile?: File) => {
    try {
      setSaving(true);
      dispatch(clearResourcesUploadProgress());

      // Create FormData with resource data and media file
      const formData = new FormData();
      
      // Append all resource fields
      formData.append('title', resourceData.title || "");
      formData.append('description', resourceData.description || "");
      formData.append('shortDescription', resourceData.shortDescription || "");
      formData.append('type', resourceData.type || "article");
      formData.append('category', resourceData.category || "general");
      formData.append('content', resourceData.content || "");
      formData.append('duration', String(resourceData.duration || 0));
      formData.append('author', resourceData.author || "");
      formData.append('source', resourceData.source || "");
      formData.append('isActive', String(resourceData.isActive !== undefined ? resourceData.isActive : true));
      formData.append('color', resourceData.color || "#667eea");
      formData.append('viewCount', "0");
      formData.append('createdBy', "admin");
      formData.append('createdAt', String(Date.now()));
      formData.append('updatedAt', String(Date.now()));
      
      // Append tags as JSON string
      if (resourceData.tags && Array.isArray(resourceData.tags)) {
        formData.append('tags', JSON.stringify(resourceData.tags));
      }
      
      // Append media file if provided, otherwise keep existing URL
      if (mediaFile) {
        const fieldName = resourceData.type === "video" ? "video" : resourceData.type === "audio" ? "audio" : "pdf";
        formData.append(fieldName, mediaFile);
      } else if (resourceData.url) {
        formData.append('url', resourceData.url);
      }
      
      const token = localStorage.getItem('authToken');
      
      // Single API call with progress tracking
      const response = await axios.post(
        `${API_BASE_URL}${endPoints.resources.create}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: token ? `Bearer ${token}` : '',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              dispatch(setResourcesUploadProgress(percentCompleted));
            }
          },
        }
      );
      
      dispatch(clearResourcesUploadProgress());
      
      if (response.data?.success || response.data) {
        const newResource = response.data?.data || response.data;
        dispatch(createResourceAction(newResource));
        toast.success("resource is created");
        navigate("/resources");
      } else {
        toast.error("Failed to create resource");
      }
    } catch (error: any) {
      console.error("Failed to create resource:", error);
      dispatch(clearResourcesUploadProgress());
      toast.error(
        `❌ Failed to create resource: ${
          error?.response?.data?.message || error?.message || "Unknown error"
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/resources");
  };

  return (
    <Box>
      <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          mb: 4,
        }}
      >
        <Typography variant='h4' fontWeight='700' gutterBottom>
          Create New Resource
        </Typography>
        <Typography variant='body1' sx={{ opacity: 0.9 }}>
          Add a new wellness resource to your library
        </Typography>
      </Paper>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <ResourceForm
          resource={null}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={saving}
          uploadProgress={uploadProgress}
        />
      </Paper>
    </Box>
  );
};

export default AddResource;

